import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DialogFixUtil } from '@/components/ui/dialog-fix-util';
import { Button } from '@/components/ui/button';
import { FileUp, Loader2, X, AlertCircle, CheckCircle, RotateCw } from 'lucide-react';
import { propertyApi } from '@/services/api';
import { CSVFileInput } from '@/components/shared/CSVFileInput';
import { useCSVUpload } from '@/hooks/use-csv-upload';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { BulkUploadResponse } from '@/types/upload';

interface PropertyBulkUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  associationId?: number;
}

export const PropertyBulkUploadModal: React.FC<PropertyBulkUploadModalProps> = ({
  open,
  onOpenChange,
  associationId
}) => {
  const queryClient = useQueryClient();

  const handleUploadSuccess = () => {
    // Refetch properties data
    queryClient.invalidateQueries({
      queryKey: ['properties']
    });
    
    // Close the modal
    onOpenChange(false);
  };

  const {
    file,
    error,
    uploading,
    importResult,
    handleFileChange,
    handleSubmit,
    handleCancel,
    resetResult
  } = useCSVUpload<BulkUploadResponse>({
    uploadFn: (file) => propertyApi.bulkUpload(file, associationId),
    onSuccess: handleUploadSuccess,
    onCancel: () => onOpenChange(false)
  });

  const handleDialogChange = (open: boolean) => {
    if (!uploading) {
      // Only allow closing if not currently uploading
      onOpenChange(open);
    }
  };

  // Handle retry after seeing errors
  const handleRetry = () => {
    resetResult();
  };

  // Handle continue after seeing errors
  const handleContinue = () => {
    if (importResult) {
      handleUploadSuccess();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogFixUtil open={open} />
      <DialogContent 
        className={`overflow-hidden ${importResult?.errors?.length ? 'max-w-3xl' : ''}`}
        onPointerDownOutside={(e) => {
          // Prevent closing when uploading
          if (uploading) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          // Prevent closing when uploading
          if (uploading) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Bulk Upload Properties</DialogTitle>
          <DialogDescription>
            Upload a CSV file with property data. Make sure your file follows the required format.
          </DialogDescription>
        </DialogHeader>
        
        {importResult?.errors && importResult.errors.length > 0 ? (
          <div className="space-y-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Import Result</AlertTitle>
              <AlertDescription>
                Successfully imported {importResult.imported} out of {importResult.total || importResult.imported + importResult.errors.length} properties.
                The following {importResult.errors.length} errors occurred:
              </AlertDescription>
            </Alert>
            
            <div className="max-h-[350px] overflow-y-auto border rounded">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">#</TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {importResult.errors.map((error, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell className="text-sm text-destructive">{error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleRetry}>
                <RotateCw className="mr-2 h-4 w-4" />
                Upload Another File
              </Button>
              <Button onClick={handleContinue}>
                <CheckCircle className="mr-2 h-4 w-4" />
                Continue With Successful Imports
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={(e) => handleSubmit(e, associationId)} encType="multipart/form-data">
            <CSVFileInput
              id="csv-file"
              error={error}
              templateUrl="/templates/properties_template.csv"
              templateFileName="properties_template.csv"
              onChange={handleFileChange}
              disabled={uploading}
              file={file}
              description="Please make sure your CSV file includes required columns: address, city, state, zip_code."
            />
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancel} 
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={!file || uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FileUp className="mr-2 h-4 w-4" />
                    Upload
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        <button
          onClick={handleCancel}
          disabled={uploading}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
      </DialogContent>
    </Dialog>
  );
};
